import React, { useState, useMemo } from 'react';
import './usertools-css/MineralZincCalculator.css';

const ZINC_REFERENCES = [
  { minAge: 1, maxAge: 3, male: 3, female: 3, upperLimit: 7 },
  { minAge: 4, maxAge: 8, male: 5, female: 5, upperLimit: 12 },
  { minAge: 9, maxAge: 13, male: 8, female: 8, upperLimit: 23 },
  {
    minAge: 14,
    maxAge: 18,
    male: 11,
    female: 9,
    pregnant: 12,
    lactating: 13,
    upperLimit: 34,
  },
  {
    minAge: 19,
    maxAge: 120,
    male: 11,
    female: 8,
    pregnant: 11,
    lactating: 12,
    upperLimit: 40,
  },
];

const ZINC_FOODS = [
  { id: 'oysters', label: 'Pacific oysters, cooked · 3 oz', zinc: 28.2 },
  { id: 'beef', label: 'Beef bottom sirloin, roasted · 3 oz', zinc: 3.8 },
  { id: 'crab', label: 'Blue crab, cooked · 3 oz', zinc: 3.2 },
  { id: 'cereal', label: 'Cereal fortified with 25% zinc DV · 1 serving', zinc: 2.8 },
  { id: 'oats', label: 'Oats, cooked in water · 1 cup', zinc: 2.3 },
  { id: 'pumpkinSeeds', label: 'Pumpkin seeds, roasted · 1 oz', zinc: 2.2 },
  { id: 'pork', label: 'Pork center loin, broiled · 3 oz', zinc: 1.9 },
  { id: 'turkey', label: 'Turkey breast, roasted · 3 oz', zinc: 1.5 },
  { id: 'cheddar', label: 'Cheddar cheese · 1.5 oz', zinc: 1.5 },
  { id: 'lentils', label: 'Lentils, boiled · ½ cup', zinc: 1.3 },
  { id: 'yogurt', label: 'Plain Greek yogurt · 6 oz', zinc: 1.0 },
  { id: 'milk', label: '1% milk · 1 cup', zinc: 1.0 },
  { id: 'kidneyBeans', label: 'Kidney beans, canned · ½ cup', zinc: 0.6 },
];

const INPUT_LIMITS = {
  maxAge: 120,
  maxWeeklyServings: 70,
  maxOtherWeeklyZinc: 1000,
  maxSupplementDose: 500,
  maxDailyDoses: 10,
  daysPerWeek: 7,
};

const RISK_FACTOR_OPTIONS = [
  ['gastrointestinalCondition', 'GI disorder or bariatric surgery'],
  ['alcoholUseDisorder', 'Alcohol use disorder'],
  ['thiazideDiuretic', 'Regular thiazide diuretic use'],
  ['highDoseIron', 'Take ≥25 mg iron with zinc supplements'],
  ['interactingMedication', 'Use quinolone/tetracycline antibiotics or penicillamine'],
  ['woundOrBurn', 'Significant wound or burn'],
];

function formatZinc(amount) {
  return amount.toFixed(1);
}

function readOptionalNumber(value, label, maximum) {
  if (value === '' || value === undefined || value === null) return 0;

  const amount = Number(value);
  if (!Number.isFinite(amount) || amount < 0 || amount > maximum) {
    throw new Error(`${label} must be between 0 and ${maximum}.`);
  }

  return amount;
}

function calculateZinc(profile) {
  const age = Number(profile.age);

  if (!Number.isInteger(age) || age < 1 || age > INPUT_LIMITS.maxAge) {
    throw new Error(
      `Enter age in whole years from 1 to ${INPUT_LIMITS.maxAge}. For infants, seek individualized feeding guidance.`
    );
  }

  if (!['male', 'female'].includes(profile.referenceSex)) {
    throw new Error('Select the reference sex used for the zinc intake table.');
  }

  if (
    profile.lifeStage !== 'none' &&
    (profile.referenceSex !== 'female' || age < 14 || age > 50)
  ) {
    throw new Error(
      'The pregnancy and lactation reference values apply to ages 14–50 in this tool.'
    );
  }

  const reference = ZINC_REFERENCES.find(
    ({ minAge, maxAge }) => age >= minAge && age <= maxAge
  );

  if (!reference) {
    throw new Error('No zinc reference value is available for this age.');
  }

  const referenceCategory =
    profile.lifeStage === 'none' ? profile.referenceSex : profile.lifeStage;
  const recommendedIntake = reference[referenceCategory];

  if (recommendedIntake === undefined) {
    throw new Error('No zinc reference value is available for this life stage.');
  }

  const hasFoodEntry =
    Object.values(profile.foodServings).some((value) => value !== '' && Number(value) > 0) ||
    (profile.otherWeeklyZinc !== '' && Number(profile.otherWeeklyZinc) > 0);

  if (!hasFoodEntry && !profile.usesSupplement) {
    throw new Error(
      'Enter at least one weekly food serving, another food amount, or a zinc supplement to calculate intake.'
    );
  }

  const listedWeeklyFoodZinc = ZINC_FOODS.reduce((total, food) => {
    const servings = readOptionalNumber(
      profile.foodServings[food.id],
      food.label,
      INPUT_LIMITS.maxWeeklyServings
    );
    return total + servings * food.zinc;
  }, 0);

  const otherWeeklyFoodZinc = readOptionalNumber(
    profile.otherWeeklyZinc,
    'Other food zinc per week',
    INPUT_LIMITS.maxOtherWeeklyZinc
  );

  const weeklyFoodZinc = listedWeeklyFoodZinc + otherWeeklyFoodZinc;

  let supplementPerDayUsed = 0;
  let supplementAverage = 0;

  if (profile.usesSupplement) {
    if (profile.supplementDose === '' || Number(profile.supplementDose) <= 0) {
      throw new Error('Enter your elemental zinc dose per serving (mg).');
    }

    const dose = readOptionalNumber(
      profile.supplementDose,
      'Elemental zinc per dose',
      INPUT_LIMITS.maxSupplementDose
    );
    const doses = readOptionalNumber(
      profile.dosesPerDay,
      'Doses per supplement day',
      INPUT_LIMITS.maxDailyDoses
    );
    const days = readOptionalNumber(
      profile.supplementDays,
      'Supplement days per week',
      INPUT_LIMITS.daysPerWeek
    );

    if (
      dose <= 0 ||
      !Number.isInteger(doses) ||
      doses < 1 ||
      !Number.isInteger(days) ||
      days < 1
    ) {
      throw new Error(
        'Enter a zinc dose above 0, whole doses per day, and 1–7 supplement days per week.'
      );
    }

    supplementPerDayUsed = dose * doses;
    supplementAverage =
      (supplementPerDayUsed * days) / INPUT_LIMITS.daysPerWeek;
  }

  const foodAverage = weeklyFoodZinc / INPUT_LIMITS.daysPerWeek;
  const loggedAverage = foodAverage + supplementAverage;
  const modeledSupplementDay = foodAverage + supplementPerDayUsed;

  return {
    recommendedIntake,
    upperLimit: reference.upperLimit,
    foodAverage,
    supplementAverage,
    loggedAverage,
    modeledSupplementDay,
    supplementPerDayUsed,
    difference: loggedAverage - recommendedIntake,
    aboveAverageUpperLimit: loggedAverage > reference.upperLimit,
    aboveSupplementDayUpperLimit:
      supplementPerDayUsed > 0 &&
      modeledSupplementDay > reference.upperLimit,
  };
}

function MineralZincCalculator() {
  const [age, setAge] = useState('');
  const [referenceSex, setReferenceSex] = useState('');
  const [lifeStage, setLifeStage] = useState('none');
  const [dietPattern, setDietPattern] = useState('mixed');
  const [foodServings, setFoodServings] = useState({});
  const [otherWeeklyZinc, setOtherWeeklyZinc] = useState('');
  const [usesSupplement, setUsesSupplement] = useState(false);
  const [supplementDose, setSupplementDose] = useState('');
  const [dosesPerDay, setDosesPerDay] = useState('1');
  const [supplementDays, setSupplementDays] = useState('7');
  const [riskFactors, setRiskFactors] = useState({
    gastrointestinalCondition: false,
    alcoholUseDisorder: false,
    thiazideDiuretic: false,
    highDoseIron: false,
    interactingMedication: false,
    woundOrBurn: false,
  });

  const hasFoodEntry =
    Object.values(foodServings).some((value) => value !== '' && Number(value) > 0) ||
    (otherWeeklyZinc !== '' && Number(otherWeeklyZinc) > 0);

  // Dynamic live calculation on every keystroke
  const calculation = useMemo(() => {
    const hasAnyInput =
      age !== '' ||
      referenceSex !== '' ||
      hasFoodEntry ||
      usesSupplement;

    if (!hasAnyInput || (!age && !referenceSex)) {
      return {
        result: null,
        error: '',
      };
    }

    if (!age) {
      return {
        result: null,
        error: 'Enter your age in whole years.',
      };
    }

    if (!referenceSex) {
      return {
        result: null,
        error: 'Select the reference sex used for the zinc intake table.',
      };
    }

    if (!hasFoodEntry && !usesSupplement) {
      return {
        result: null,
        error: '',
      };
    }

    try {
      const calculated = calculateZinc({
        age,
        referenceSex,
        lifeStage,
        foodServings,
        otherWeeklyZinc,
        usesSupplement,
        supplementDose,
        dosesPerDay,
        supplementDays,
      });
      return { result: calculated, error: '' };
    } catch (err) {
      return {
        result: null,
        error: err instanceof Error ? err.message : 'Please review your entries.',
      };
    }
  }, [
    age,
    referenceSex,
    lifeStage,
    hasFoodEntry,
    foodServings,
    otherWeeklyZinc,
    usesSupplement,
    supplementDose,
    dosesPerDay,
    supplementDays,
  ]);

  const { result, error: validationError } = calculation;
  const missingInputs = [
    !age && 'your age',
    !referenceSex && 'your reference sex',
    !hasFoodEntry && !usesSupplement && 'your weekly zinc intake from food or supplements',
  ].filter(Boolean);
  const missingInputText = missingInputs.length > 1
    ? `${missingInputs.slice(0, -1).join(', ')} and ${missingInputs[missingInputs.length - 1]}`
    : missingInputs[0];

  function updateRiskFactor(event) {
    const { name, checked } = event.target;
    setRiskFactors((previous) => ({ ...previous, [name]: checked }));
  }

  const riskNotes = [
    (dietPattern === 'vegetarian' || dietPattern === 'vegan') &&
      'Plant-based diets can provide less absorbable zinc because phytates bind zinc. Soaking, fermenting, or leavening legumes and grains may help. This tool does not assign a fixed extra dose.',
    riskFactors.gastrointestinalCondition &&
      'Gastrointestinal disease or bariatric surgery can increase the risk of zinc inadequacy. A clinician can assess your individual needs.',
    riskFactors.alcoholUseDisorder &&
      'Alcohol use disorder is associated with reduced zinc status and may affect absorption and losses. Seek individualized care.',
    riskFactors.thiazideDiuretic &&
      'Thiazide diuretics can increase urinary zinc excretion. Discuss regular use with your clinician.',
    riskFactors.highDoseIron &&
      'Taking at least 25 mg elemental iron at the same time as a zinc supplement may reduce zinc absorption. Ask a pharmacist about timing.',
    riskFactors.interactingMedication &&
      'Quinolone or tetracycline antibiotics and penicillamine can interact with zinc supplements. Check the specific medicine and dosing schedule with a pharmacist.',
    riskFactors.woundOrBurn &&
      'A wound or burn may need individual nutrition care. No general additional zinc dose is calculated here.',
  ].filter(Boolean);

  let badge = {
    className: 'zn-badge-green',
    label: 'Logged intake reaches RDA',
  };

  if (result?.aboveAverageUpperLimit) {
    badge = {
      className: 'zn-badge-red',
      label: 'Average above UL estimate',
    };
  } else if (result?.aboveSupplementDayUpperLimit) {
    badge = {
      className: 'zn-badge-orange',
      label: 'Review supplement day',
    };
  } else if (result && result.difference < 0) {
    badge = {
      className: 'zn-badge-orange',
      label: 'Logged intake below RDA',
    };
  }

  return (
    <div className="mineralZincCalculator">
      <header className="zn-hero">
        <div>
          <p className="zn-eyebrow">Pure Health Nexus / Mineral Tools</p>
          <h1>Understand your zinc intake.</h1>
          <p className="zn-hero-desc">
            Compare a representative weekly food log and your supplements
            with age and life-stage zinc references. Intake estimates cannot
            diagnose zinc deficiency.
          </p>
          <div className="zn-promo-banner">
            <strong>Unlock Smarter Eating:</strong> Explore vitamins,
            minerals, and nutrition with a single{' '}
            <a
              className="zn-promo-link"
              href="https://purehealthnexus.com/ai-nutrition-vitamins-minerals"
            >
              AI scan
            </a>
          </div>
        </div>

        <div className="zn-hero-art" aria-hidden="true">
          <img
            src="/assets/images/appszinc.webp"
            alt="Zinc Calculator Icon"
            className="tool-icon"
            loading="lazy"
            style={{ width: '120px', height: '120px', objectFit: 'contain', borderRadius: 0, boxShadow: 'none' }}
          />
          <span className="zn-art-caption">ESSENTIAL MINERAL</span>
        </div>
      </header>

      <div className="zn-layout">
        <form
          className="zn-panel zn-input-panel"
          onSubmit={(event) => event.preventDefault()}
          noValidate
        >
          <div className="zn-panel-heading">
            <div>
              <p className="zn-eyebrow">01 / Profile</p>
              <h2>Your Details</h2>
              <p>Type your stats below. Results update live as you enter data.</p>
            </div>
            <span className="zn-step">01</span>
          </div>

          <div className="zn-field-row">
            <label className="zn-field">
              <span className="zn-field-label">Age in whole years</span>
              <input
                type="number"
                min="1"
                max={INPUT_LIMITS.maxAge}
                step="1"
                value={age}
                onChange={(event) => setAge(event.target.value)}
                placeholder="e.g. 35"
              />
            </label>

            <label className="zn-field">
              <span className="zn-field-label">Biological sex</span>
              <select
                value={referenceSex}
                onChange={(event) => {
                  setReferenceSex(event.target.value);
                  setLifeStage('none');
                }}
              >
                <option value="">Select</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
              </select>
            </label>
          </div>

          <p className="zn-hint">
            The US reference table has male and female categories. Select
            the one your clinician uses for nutrition guidance if uncertain.
          </p>

          {referenceSex === 'female' &&
            Number(age) >= 14 &&
            Number(age) <= 50 && (
              <label className="zn-field">
                <span className="zn-field-label">Life stage</span>
                <select
                  value={lifeStage}
                  onChange={(event) => setLifeStage(event.target.value)}
                >
                  <option value="none">
                    Neither pregnancy nor lactation
                  </option>
                  <option value="pregnant">Pregnant</option>
                  <option value="lactating">Lactating</option>
                </select>
              </label>
            )}

          <div className="zn-section-label">Diet context</div>
          <label className="zn-field">
            <span className="zn-field-label">Diet pattern</span>
            <select
              value={dietPattern}
              onChange={(event) => setDietPattern(event.target.value)}
            >
              <option value="mixed">Mixed diet</option>
              <option value="pescatarian">Pescatarian</option>
              <option value="vegetarian">Vegetarian</option>
              <option value="vegan">Vegan</option>
            </select>
          </label>
          <p className="zn-hint">
            Diet pattern adds context; it does not automatically change
            the published RDA.
          </p>

          <div className="zn-section-label">
            Weekly food servings <small>Typical week</small>
          </div>
          <div className="zn-diet-grid">
            {ZINC_FOODS.map((food) => (
              <label className="zn-diet-item" key={food.id}>
                <span>{food.label}</span>
                <input
                  type="number"
                  min="0"
                  max={INPUT_LIMITS.maxWeeklyServings}
                  step="any"
                  value={foodServings[food.id] ?? ''}
                  onChange={(event) =>
                    setFoodServings((previous) => ({
                      ...previous,
                      [food.id]: event.target.value,
                    }))
                  }
                  placeholder="0"
                />
                <small>~{food.zinc} mg per serving</small>
              </label>
            ))}
          </div>

          <label className="zn-field">
            <span className="zn-field-label">
              Other food zinc (mg/week)
            </span>
            <input
              type="number"
              min="0"
              max={INPUT_LIMITS.maxOtherWeeklyZinc}
              step="any"
              value={otherWeeklyZinc}
              onChange={(event) =>
                setOtherWeeklyZinc(event.target.value)
              }
              placeholder="From labels or a food diary"
            />
          </label>
          <p className="zn-hint">
            Food values are examples and vary by product and preparation.
            Use the label for fortified foods, and include other foods in
            the field above. Avoid counting a food twice.
          </p>

          <div className="zn-section-label">Supplements</div>
          <label className="zn-checkbox-label">
            <input
              type="checkbox"
              checked={usesSupplement}
              onChange={(event) =>
                setUsesSupplement(event.target.checked)
              }
            />
            I take zinc in a supplement or multivitamin
          </label>

          {usesSupplement && (
            <div className="zn-supplement-fields">
              <label className="zn-field">
                <span className="zn-field-label">
                  Elemental zinc per dose (mg)
                </span>
                <input
                  type="number"
                  min="0"
                  max={INPUT_LIMITS.maxSupplementDose}
                  step="any"
                  value={supplementDose}
                  onChange={(event) =>
                    setSupplementDose(event.target.value)
                  }
                  placeholder="Check Supplement Facts"
                />
              </label>

              <div className="zn-field-row">
                <label className="zn-field">
                  <span className="zn-field-label">
                    Doses on days used
                  </span>
                  <input
                    type="number"
                    min="1"
                    max={INPUT_LIMITS.maxDailyDoses}
                    step="1"
                    value={dosesPerDay}
                    onChange={(event) =>
                      setDosesPerDay(event.target.value)
                    }
                  />
                </label>
                <label className="zn-field">
                  <span className="zn-field-label">
                    Days used per week
                  </span>
                  <input
                    type="number"
                    min="1"
                    max={INPUT_LIMITS.daysPerWeek}
                    step="1"
                    value={supplementDays}
                    onChange={(event) =>
                      setSupplementDays(event.target.value)
                    }
                  />
                </label>
              </div>
              <p className="zn-hint">
                Enter elemental zinc, not the weight of zinc gluconate
                or another compound. Include zinc in multivitamins and
                lozenges.
              </p>
            </div>
          )}

          <div className="zn-section-label">
            Factors to discuss <small>No numeric adjustment</small>
          </div>
          {RISK_FACTOR_OPTIONS.map(([name, label]) => (
            <label className="zn-checkbox-label" key={name}>
              <input
                type="checkbox"
                name={name}
                checked={riskFactors[name]}
                onChange={updateRiskFactor}
              />
              {label}
            </label>
          ))}
        </form>

        <main className="zn-results" id="zinc-results">
          {!result ? (
            <section className="zn-panel zn-result-hero">
              <div className="zn-result-top">
                <div>
                  <p className="zn-eyebrow">02 / Assessment</p>
                  <h2>Weekly intake view</h2>
                </div>
                <span className="zn-badge zn-badge-orange">
                  {validationError ? 'Review entries' : 'Awaiting input'}
                </span>
              </div>
              {validationError ? (
                <p className="zn-error-notice" role="alert">
                  {validationError}
                </p>
              ) : (
                <p className="zn-muted">
                  Enter {missingInputText} to view your zinc intake analysis.
                </p>
              )}
            </section>
          ) : (
            <>
              <section className="zn-panel zn-result-hero">
                <div className="zn-result-top">
                  <div>
                    <p className="zn-eyebrow">02 / Assessment</p>
                    <h2>Estimated logged intake</h2>
                  </div>
                  <span className={`zn-badge ${badge.className}`}>
                    {badge.label}
                  </span>
                </div>

                <div className="zn-metrics-grid">
                  <div>
                    <span className="zn-metric-label">Zinc RDA</span>
                    <div className="zn-metric-val">
                      {result.recommendedIntake}
                      <span> mg/day</span>
                    </div>
                    <p className="zn-metric-sub">
                      US dietary reference intake
                    </p>
                  </div>
                  <div>
                    <span className="zn-metric-label">
                      Logged weekly average
                    </span>
                    <div className="zn-metric-val">
                      {formatZinc(result.loggedAverage)}
                      <span> mg/day</span>
                    </div>
                    <p className="zn-metric-sub">
                      {result.difference < 0
                        ? `${formatZinc(Math.abs(result.difference))} mg/day below RDA`
                        : `${formatZinc(result.difference)} mg/day above RDA`}{' '}
                      in this log
                    </p>
                  </div>
                </div>

                <div className="zn-progress-wrap">
                  <div
                    className="zn-progress-track"
                    role="img"
                    aria-label={`Logged weekly average ${formatZinc(result.loggedAverage)} milligrams per day versus RDA ${result.recommendedIntake} milligrams per day`}
                  >
                    <div
                      className="zn-progress-fill-diet"
                      style={{
                        width: `${Math.min(
                          100,
                          (result.foodAverage /
                            result.recommendedIntake) *
                            100
                        )}%`,
                      }}
                    />
                    <div
                      className="zn-progress-fill-supp"
                      style={{
                        width: `${Math.min(
                          Math.max(
                            0,
                            100 -
                              (result.foodAverage /
                                result.recommendedIntake) *
                                100
                          ),
                          (result.supplementAverage /
                            result.recommendedIntake) *
                            100
                        )}%`,
                      }}
                    />
                  </div>
                  <div className="zn-progress-legend">
                    <span>
                      <i className="zn-dot zn-dot-diet" />
                      Food: {formatZinc(result.foodAverage)} mg/day
                    </span>
                    <span>
                      <i className="zn-dot zn-dot-supp" />
                      Supplements: {formatZinc(result.supplementAverage)} mg/day
                    </span>
                  </div>
                </div>

                <div className="zn-stats">
                  <div>
                    <small>Upper limit (food + supplements)</small>
                    <strong>{result.upperLimit} mg/day</strong>
                  </div>
                  <div>
                    <small>Modeled supplement day</small>
                    <strong>
                      {formatZinc(result.modeledSupplementDay)} mg
                    </strong>
                  </div>
                  <div>
                    <small>Zinc from supplements on days used</small>
                    <strong>
                      {formatZinc(result.supplementPerDayUsed)} mg
                    </strong>
                  </div>
                </div>
              </section>

              {result.aboveAverageUpperLimit && (
                <div className="zn-error-notice">
                  <strong>Upper-limit alert:</strong> Your logged weekly
                  average exceeds the age-specific UL for zinc from food
                  plus supplements. Review repeated high intake with a
                  clinician. A prescribed treatment regimen requires
                  supervision.
                </div>
              )}

              {!result.aboveAverageUpperLimit &&
                result.aboveSupplementDayUpperLimit && (
                  <div className="zn-warning-notice">
                    <strong>Supplement-day review:</strong> The modeled
                    supplement day is above the UL number. The UL concerns
                    usual intake; one occasional day above it does not
                    establish harm. Review repeated high-dose use with a
                    clinician.
                  </div>
                )}

              <section className="zn-panel zn-module">
                <div className="zn-module-heading">
                  <p className="zn-eyebrow">03 / Analytics</p>
                  <h2>Intake breakdown</h2>
                  <p>
                    Arithmetic from the foods and supplements you entered.
                  </p>
                </div>
                <div className="zn-table-wrap">
                  <table className="zn-table">
                    <thead>
                      <tr>
                        <th>Component</th>
                        <th>Estimate</th>
                        <th>How it is used</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Reference intake (RDA)</td>
                        <td>{result.recommendedIntake} mg/day</td>
                        <td>Age and life-stage planning value</td>
                      </tr>
                      <tr>
                        <td>Logged foods</td>
                        <td>
                          {formatZinc(result.foodAverage)} mg/day
                        </td>
                        <td>Weekly servings divided by seven</td>
                      </tr>
                      <tr>
                        <td>Logged supplements</td>
                        <td>
                          {formatZinc(result.supplementAverage)} mg/day
                        </td>
                        <td>
                          Dose × doses × days per week ÷ seven
                        </td>
                      </tr>
                      <tr>
                        <td>Logged average</td>
                        <td>
                          {formatZinc(result.loggedAverage)} mg/day
                        </td>
                        <td>
                          Foods plus supplements; no absorption adjustment
                        </td>
                      </tr>
                      <tr>
                        <td>Modeled supplement day</td>
                        <td>
                          {formatZinc(result.modeledSupplementDay)} mg
                        </td>
                        <td>
                          Average food estimate plus full supplement-day dose
                        </td>
                      </tr>
                      <tr>
                        <td>Upper limit (UL)</td>
                        <td>{result.upperLimit} mg/day</td>
                        <td>
                          Food plus supplements for generally healthy people
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="zn-panel zn-module">
                <div className="zn-module-heading">
                  <p className="zn-eyebrow">04 / Strategy</p>
                  <h2>Evidence-based next steps</h2>
                </div>
                <div className="zn-cards-grid">
                  <div className="zn-card">
                    <h3>Build a complete food log</h3>
                    <p>
                      Include foods missing from the examples and read
                      fortified cereal labels. A partial log can
                      underestimate intake. Plant foods supply zinc,
                      although phytate can reduce absorption.
                    </p>
                  </div>
                  <div className="zn-card">
                    <h3>Review supplement labels</h3>
                    <p>
                      Count elemental zinc in multivitamins and lozenges.
                      A weekly average can hide a high dose on the day it
                      is taken; review repeated high doses with a clinician.
                    </p>
                  </div>
                </div>
              </section>
            </>
          )}

          <section className="zn-panel zn-module">
            <p className="zn-eyebrow">05 / Considerations</p>
            <h2>Absorption and care notes</h2>
            {riskNotes.length ? (
              <ul className="zn-note-list">
                {riskNotes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            ) : (
              <p className="zn-muted">
                Select relevant factors in the form to see evidence-based
                context. No fixed absorption percentage is applied.
              </p>
            )}
            {usesSupplement && (
              <p className="zn-warning-notice">
                Prolonged high-dose zinc use can interfere with copper
                absorption. Taking copper does not cancel the zinc upper
                limit; discuss a prolonged regimen with your clinician.
              </p>
            )}
          </section>
        </main>
      </div>

      <section
        className="zn-guide-section"
        aria-labelledby="zinc-guide-heading"
      >
        <div className="zn-module-heading">
          <p className="zn-eyebrow">Educational reference</p>
          <h2 id="zinc-guide-heading">Zinc, food, and safe intake</h2>
          <p>Published reference values and limits of this estimate.</p>
        </div>

        <div className="zn-def-grid">
          <article className="zn-def-card">
            <h3>1. Essential functions</h3>
            <p>
              Zinc supports enzyme activity, immune function, protein and
              DNA synthesis, wound healing, growth, and taste.
            </p>
          </article>
          <article className="zn-def-card">
            <h3>2. Dietary absorption</h3>
            <p>
              Legumes, whole grains, nuts, and seeds provide zinc, but
              phytates can reduce its absorption. This tool cannot
              calculate an individual absorption percentage.
            </p>
          </article>
          <article className="zn-def-card">
            <h3>3. RDA is a planning value</h3>
            <p>
              The RDA is intended to meet the needs of nearly all healthy
              people in a specified group. A food log below it does not
              establish deficiency.
            </p>
          </article>
          <article className="zn-def-card">
            <h3>4. Excess and copper</h3>
            <p>
              Prolonged high zinc supplement intake can impair copper
              absorption. The UL covers zinc from food and supplements
              for generally healthy people.
            </p>
          </article>
        </div>

        <div className="zn-ref-grid">
          <div className="zn-ref-block">
            <h3>Recommended dietary allowances</h3>
            <ul className="zn-ref-list">
              <li><span>1–3 years</span><strong>3 mg/day</strong></li>
              <li><span>4–8 years</span><strong>5 mg/day</strong></li>
              <li><span>9–13 years</span><strong>8 mg/day</strong></li>
              <li><span>14–18: male / female</span><strong>11 / 9 mg</strong></li>
              <li><span>19+: male / female</span><strong>11 / 8 mg</strong></li>
              <li><span>Pregnancy: 14–18 / 19–50</span><strong>12 / 11 mg</strong></li>
              <li><span>Lactation: 14–18 / 19–50</span><strong>13 / 12 mg</strong></li>
            </ul>
          </div>
          <div className="zn-ref-block">
            <h3>Upper intake levels</h3>
            <ul className="zn-ref-list">
              <li><span>1–3 years</span><strong>7 mg/day</strong></li>
              <li><span>4–8 years</span><strong>12 mg/day</strong></li>
              <li><span>9–13 years</span><strong>23 mg/day</strong></li>
              <li><span>14–18 years</span><strong>34 mg/day</strong></li>
              <li><span>19+ years</span><strong>40 mg/day</strong></li>
              <li><span>Pregnancy / lactation</span><strong>Same age UL</strong></li>
            </ul>
          </div>
        </div>

        <div className="zn-sources">
          <strong>Source and method:</strong>{' '}
          <a href="https://ods.od.nih.gov/factsheets/Zinc-HealthProfessional/">
            NIH Office of Dietary Supplements, Zinc Fact Sheet for Health
            Professionals
          </a>
          {' '}(RDA, UL, food examples, and interactions). The tool divides
          weekly logged food and supplements by seven. It does not estimate
          absorbed zinc or interpret blood tests. For infants under one
          year, consult a pediatric clinician.
        </div>
      </section>
    </div>
  );
}

export default MineralZincCalculator;
